// src/views/Tarjetas/Configuracion/ConfigurarTarjeta.jsx
import React, { useEffect, useRef, useState } from "react";
import { FaLock, FaWrench } from "react-icons/fa";
import PermisoTarjeta from "./PermisoTarjeta";
import FiltroConfig from "./FiltroConfig";
import useEscClose from "../../useEscClose";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";

export default function ConfigurarTarjeta() {
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  useEscClose(() => {
    setMostrarFiltros(false);
    document.activeElement?.blur();
  }, mostrarFiltros);

  const [mostrarPermisos, setMostrarPermisos] = useState(false);
  const [tarjetaSel, setTarjetaSel] = useState(null);

  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [paginaActual, setPaginaActual] = useState(1);
  const [porPagina, setPorPagina] = useState(30);
  const [totalElementos, setTotalElementos] = useState(0);

  // Filtros aplicados (payload desde FiltroConfig)
  // { clienteId?, clienteNombre?, tarjetaNumero?, tarjetaId?, vehiculoId?, vehiculoIdsCsv?, patente? }
  const [filtros, setFiltros] = useState(null);
  const firstRef = useRef(false);

  const totalPaginas = Math.max(1, Math.ceil(totalElementos / porPagina));
  const inicioMostrado = totalElementos === 0 ? 0 : (paginaActual - 1) * porPagina + 1;
  const finMostrado = Math.min(paginaActual * porPagina, totalElementos);

  // Normaliza los filtros al formato que entiende el backend (usa listUrl)
  function buildListUrl(page1, size, f) {
    const payload = {
      page: page1 - 1, // backend 0-based
      size,
    };

    if (f?.clienteId) payload.clienteId = Number(f.clienteId);

    // Si mandan explícitamente el id de tarjeta, úsalo
    if (f?.tarjetaId) payload.tarjetaId = Number(f.tarjetaId);

    // Si vino número, pásalo como "numero"
    if (f?.tarjetaNumero) payload.tarjetaNumero = String(f.tarjetaNumero);

    // Vehículo: prioriza vehiculoId; si no, toma primero del CSV
    if (f?.vehiculoId) {
      payload.vehiculoId = Number(f.vehiculoId);
    } else if (f?.vehiculoIdsCsv) {
      const firstVehId = String(f.vehiculoIdsCsv)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)[0];
      if (firstVehId) payload.vehiculoId = Number(firstVehId);
    }

    // ⚠️ NO enviamos 'patente' (el backend no filtra por LIKE de patente hoy)

    return apiRoutes.tarjetas.listUrl(payload);
  }

  async function cargar(page1 = 1, size = porPagina, filtrosArg = filtros) {
    setCargando(true);
    setErrorMsg("");
    try {
      const url = buildListUrl(page1, size, filtrosArg || null);
      const res = await appFetchJson(url, { method: "GET" });

      const list = Array.isArray(res?.items)
        ? res.items
        : Array.isArray(res?.content)
        ? res.content
        : [];
      const total = Number(res?.totalElements ?? res?.total ?? list.length) || 0;

      const rows = list.map((t) => {
        const id = t?.id ?? t?.tarjetaId ?? null;
        const numero = t?.numero ?? t?.tarjetaNumero ?? "—";

        // Vehículo / Patente
        const vehiculo = t?.vehiculoMarca ?? t?.vehiculo?.marcaNombre ?? "—";
        const patente =
          t?.chapa ?? t?.vehiculo?.patente ?? t?.vehiculoPatente ?? "—";

        const consumo =
          t?.consumoLt100Km ?? t?.consumo ?? t?.consumoPromedio ?? "—";
        const kmUltMant =
          t?.kilometrajeUltimoMantenimiento ?? t?.kmUltimoMantenimiento ?? "—";
        const alertarKm =
          t?.alertarCadaKm ?? t?.alertarEnKm ?? t?.alertarKm ?? "—";
        const estado = t?.estadoNombre ?? t?.estado ?? t?.estadoTarjeta ?? "—";

        const marca = t?.vehiculoMarca ?? t?.vehiculo?.marcaNombre ?? "";
        const modelo = t?.vehiculoModelo ?? t?.vehiculo?.modelo ?? "";
        const patLab = t?.chapa ?? t?.vehiculo?.patente ?? "";
        const vehiculoEtiqueta =
          patLab && (marca || modelo)
            ? `${patLab} — ${[marca, modelo].filter(Boolean).join(" ")}`
            : patLab || [marca, modelo].filter(Boolean).join(" ") || "—";

        const clienteNombre =
          t?.cliente?.nombre ?? t?.clienteNombre ?? t?.cliente?.razonSocial ?? "—";

        const clienteId = t?.clienteId ?? t?.cliente?.id ?? null;
        const codigoTipo = t?.codigoTipo ?? t?.tipo ?? null;

        const valorLimite =
          t?.valorLimite ?? t?.tarjVlLimite ?? t?.vlLimite ?? t?.limite ?? null;

        return {
          id,
          numero,
          vehiculo,
          patente,
          consumo: consumo === "—" ? "—" : Number(consumo).toFixed(1),
          kmUltMant:
            kmUltMant === "—" ? "—" : Number(kmUltMant).toLocaleString("es-PY"),
          alertarKm: alertarKm === "—" ? "—" : String(alertarKm),
          estado: estado || "—",
          clienteNombre,
          vehiculoEtiqueta,
          clienteId,
          codigoTipo,
          valorLimite,
        };
      });

      setItems(rows);
      setTotalElementos(total);
    } catch (err) {
      setErrorMsg(err?.message || "Error al cargar tarjetas");
      setItems([]);
      setTotalElementos(0);
    } finally {
      setCargando(false);
    }
  }

  // Primera carga
  useEffect(() => {
    if (!firstRef.current) {
      firstRef.current = true;
      cargar(1, porPagina, filtros);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const manejarCambioCantidad = (e) => {
    const nuevo = Number(e.target.value);
    setPorPagina(nuevo);
    setPaginaActual(1);
    cargar(1, nuevo);
  };

  const cambiarPagina = (num) => {
    if (num < 1 || num > totalPaginas) return;
    setPaginaActual(num);
    cargar(num, porPagina);
  };

  // Aplicar / limpiar filtros
  const aplicarFiltros = (payload) => {
    setFiltros(payload || null);
    setPaginaActual(1);
    setMostrarFiltros(false);
    cargar(1, porPagina, payload || null);
  };

  const limpiarFiltros = () => {
    setFiltros(null);
    setPaginaActual(1);
    cargar(1, porPagina, null);
  };

  // ===== Vista Permisos =====
  if (mostrarPermisos && tarjetaSel) {
    return (
      <PermisoTarjeta
        tarjetaId={tarjetaSel.id}
        numeroTarjeta={tarjetaSel.numero}
        clienteNombre={tarjetaSel.clienteNombre ?? "—"}
        chapa={tarjetaSel.patente}
        vehiculoMarca={tarjetaSel.vehiculo}
        vehiculoEtiqueta={tarjetaSel.vehiculoEtiqueta}
        clienteId={tarjetaSel.clienteId}
        codigoTipo={tarjetaSel.codigoTipo}
        valorLimite={tarjetaSel.valorLimite}
        onVolver={() => setMostrarPermisos(false)}
      />
    );
  }

  // ---- Helper para armar el "inicial" del FiltroConfig (con IDs persistentes) ----
  const inicialFiltros = (() => {
    if (!filtros) return null;

    // Resolver vehiculoId a partir de vehiculoId o primer CSV
    const vehiculoId =
      filtros?.vehiculoId ??
      (filtros?.vehiculoIdsCsv
        ? Number(String(filtros.vehiculoIdsCsv).split(",").map(s => s.trim()).filter(Boolean)[0])
        : null);

    const tarjetaSel =
      (filtros?.tarjetaNumero || filtros?.tarjetaId)
        ? {
            id: filtros?.tarjetaId ?? null,
            numero: filtros?.tarjetaNumero ?? "",
            vehiculo: vehiculoId ? { id: vehiculoId } : null,
          }
        : null;

    return {
      clienteId: filtros?.clienteId ?? null,
      clienteNombre: filtros?.clienteNombre ?? null,
      tarjetaSel,                // usado por el input de tarjeta
      tarjetaId: filtros?.tarjetaId ?? null, // ID persistente para re-aplicar
      vehiculoId: vehiculoId ?? null,        // ID persistente para re-aplicar
      patente: filtros?.patente ?? "",
    };
  })();

  return (
    <div className="page">
      <h1 className="page-title">💳 Configuración de Tarjetas</h1>

      <div className="toolbar">
        <div className="inline-flex items-center gap-2">
          <button
            className="btn-fil"
            onClick={() => setMostrarFiltros((v) => !v)}
          >
            {mostrarFiltros ? "Ocultar Filtros" : "Ver Filtros"}
          </button>

          {/* Chips resumen de filtros activos */}
          {filtros && (
            // <div className="flex flex-wrap items-center gap-2 ml-3">
            //   {filtros.clienteNombre && (
            //     <span className="chip">Cliente: {filtros.clienteNombre}</span>
            //   )}
            //   {filtros.tarjetaNumero && (
            //     <span className="chip">Tarjeta: {filtros.tarjetaNumero}</span>
            //   )}
            //   {(() => {
            //     const vehId =
            //       filtros?.vehiculoId ??
            //       (filtros?.vehiculoIdsCsv
            //         ? Number(String(filtros.vehiculoIdsCsv).split(",")[0])
            //         : null);
            //     return vehId ? <span className="chip">Vehículo #{vehId}</span> : null;
            //   })()}
              
            // </div>
            <button className="btn-fil" onClick={limpiarFiltros}>
                Limpiar Seleccion
              </button>
          )}
        </div>
      </div>

      <div className="control-block">
        <label htmlFor="pp" className="label-strong">
          Mostrar por página:
        </label>
        <select
          id="pp"
          value={porPagina}
          onChange={manejarCambioCantidad}
          className="select"
        >
          <option value={30}>30</option>
          <option value={40}>40</option>
          <option value={50}>50</option>
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="table-card table-sm">
          <thead className="table-head">
            <tr>
              <th className="cell text-left">Nro de Tarjeta</th>
              <th className="cell text-left">Vehículo</th>
              <th className="cell text-left">Patente</th>
              <th className="cell text-left">Consumo(Lt/100Km)</th>
              <th className="cell text-left">Km último mant.</th>
              <th className="cell text-left">Alertar en (Km)</th>
              <th className="cell text-left">Estado</th>
              <th className="cell text-left">Acciones</th>
            </tr>
          </thead>

          <tbody>
            {cargando ? (
              <tr>
                <td colSpan={8} className="cell-center py-4">
                  Cargando…
                </td>
              </tr>
            ) : errorMsg ? (
              <tr>
                <td colSpan={8} className="cell-center text-red-600 py-4">
                  {errorMsg}
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={8} className="cell-center text-gray-500 py-4">
                  Ningún resultado encontrado.
                </td>
              </tr>
            ) : (
              items.map((r, idx) => (
                <tr
                  key={`${r.id ?? r.numero}-${idx}`}
                  className={idx % 2 === 0 ? "row-alt" : "bg-white"}
                >
                  <td className="cell">{r.numero}</td>
                  <td className="cell">{r.vehiculo}</td>
                  <td className="cell">{r.patente}</td>
                  <td className="cell">{r.consumo}</td>
                  <td className="cell">{r.kmUltMant}</td>
                  <td className="cell">{r.alertarKm}</td>
                  <td className="cell">{r.estado}</td>
                  <td className="cell-center">
                    <div className="inline-flex items-center gap-2 text-green-700">
                      <button
                        type="button"
                        className="icon-btn"
                        title="Permisos / Bloqueo"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTarjetaSel(r);
                          setMostrarPermisos(true);
                        }}
                      >
                        <FaLock />
                      </button>

                      {/*
                      <button
                        type="button"
                        className="icon-btn"
                        title="Mantenimiento"
                        onClick={(e) => {
                          e.stopPropagation();
                          alert(`Configurar ${r.numero}`);
                        }}
                      >
                        <FaWrench />
                      </button>
                      */}

                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {mostrarFiltros && (
        <FiltroConfig
          onClose={() => setMostrarFiltros(false)}
          onAplicar={aplicarFiltros}
          inicial={inicialFiltros}
        />
      )}

      <div className="mt-4 text-sm text-gray-600">
        {`${totalElementos.toLocaleString("es-ES")} item${
          totalElementos !== 1 ? "s" : ""
        } encontrados, mostrando de ${inicioMostrado} a ${finMostrado}.`}
      </div>

      <nav className="pager">
        <button
          onClick={() => cambiarPagina(1)}
          disabled={paginaActual === 1}
          className={paginaActual === 1 ? "pager-off" : "pager-btn"}
        >
          Primera
        </button>
        <button
          onClick={() => cambiarPagina(paginaActual - 1)}
          disabled={paginaActual === 1}
          className={paginaActual === 1 ? "pager-off" : "pager-btn"}
        >
          Anterior
        </button>

        {[...Array(totalPaginas)].map((_, i) => {
          const num = i + 1;
          if (
            num === 1 ||
            num === totalPaginas ||
            (num >= paginaActual - 2 && num <= paginaActual + 2)
          ) {
            return (
              <button
                key={num}
                onClick={() => cambiarPagina(num)}
                className={paginaActual === num ? "pager-active" : "pager-btn"}
              >
                {num}
              </button>
            );
          }
          if (num === paginaActual - 3 || num === paginaActual + 3) {
            return (
              <span key={num} className="px-2 py-1">
                …
              </span>
            );
          }
          return null;
        })}

        <button
          onClick={() => cambiarPagina(paginaActual + 1)}
          disabled={paginaActual === totalPaginas || totalPaginas === 0}
          className={
            paginaActual === totalPaginas || totalPaginas === 0
              ? "pager-off"
              : "pager-btn"
          }
        >
          Próxima
        </button>
        <button
          onClick={() => cambiarPagina(totalPaginas)}
          disabled={paginaActual === totalPaginas || totalPaginas === 0}
          className={
            paginaActual === totalPaginas || totalPaginas === 0
              ? "pager-off"
              : "pager-btn"
          }
        >
          Última
        </button>
      </nav>
    </div>
  );
}
